import { LiveTrainingCardProps, TrainingStatus } from 'src/components/LiveTrainingCard';
import { FlattenedLearningInstance } from 'src/utils/almDataTransformer';
import { ISODateTimeString } from 'src/utils/date';

/**
 * Mock data for Live Training sessions to be used with LiveTrainingCard component
 */

/**
 * Extended type for mock data that includes additional fields needed for testing
 */
export type LiveTrainingCardMockProps = LiveTrainingCardProps & {
  instanceId: string;
  courseId: string;
  label?: string;
  courseMaterialsUrl: string;
};

// Individual training session mocks
export const upcomingJiraAdminTraining: LiveTrainingCardMockProps = {
  instanceId: '100001',
  courseId: 'course-jira-admin-001',
  title: 'Advanced Jira Administration',
  subtitle:
    'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.',
  startDateTime: '2025-11-15T10:00:00-08:00' as ISODateTimeString, // November 15, 2025 at 10:00 AM PST
  endDateTime: '2025-11-15T12:00:00-08:00' as ISODateTimeString, // 2 hours
  status: 'upcoming' as TrainingStatus,
  enrolled: true,
  products: ['Jira'],
  label: 'Team training',
  host: 'John Smith',
  showAddToCalendar: true,
  showDownloadMaterials: false,
  courseMaterialsUrl: 'https://university.atlassian.com/student/path/1234567/materials',
  videoCallUrl: 'https://meet.google.com/abc-defg-hij',
  isFree: false,
  isSaved: false,
  isPrivate: true,
};

export const upcomingConfluenceTraining: LiveTrainingCardMockProps = {
  instanceId: '100002',
  courseId: 'course-confluence-fund-001',
  title: 'Confluence Fundamentals',
  subtitle:
    'Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo. Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit.',
  startDateTime: '2025-11-20T14:00:00-08:00' as ISODateTimeString, // November 20, 2025 at 2:00 PM PST
  endDateTime: '2025-11-20T17:00:00-08:00' as ISODateTimeString, // 3 hours
  status: 'upcoming' as TrainingStatus,
  enrolled: true,
  products: ['Confluence'],
  label: 'Public class',
  host: 'Jane Doe',
  showAddToCalendar: true,
  showDownloadMaterials: false,
  courseMaterialsUrl: 'https://university.atlassian.com/student/path/2345678/materials',
  videoCallUrl: 'https://zoom.us/j/123456789',
  isFree: true,
  isSaved: false,
  isPrivate: false,
};

export const upcomingJSMTraining: LiveTrainingCardMockProps = {
  instanceId: '100003',
  courseId: 'course-jsm-ess-001',
  title: 'Jira Service Management Essentials',
  subtitle: 'ITSM best practices and implementation strategies with Jira Service Management',
  startDateTime: '2025-12-01T09:00:00-08:00' as ISODateTimeString, // December 1, 2025 at 9:00 AM PST
  endDateTime: '2025-12-01T10:00:00-08:00' as ISODateTimeString, // 1 hour
  status: 'upcoming' as TrainingStatus,
  enrolled: false,
  products: ['Jira Service Management'],
  label: 'Public class',
  host: 'Mike Johnson',
  showAddToCalendar: true,
  showDownloadMaterials: false,
  courseMaterialsUrl: 'https://university.atlassian.com/student/path/3456789/materials',
  videoCallUrl: 'https://meet.google.com/xyz-uvwx-rst',
  isFree: true,
  isSaved: false,
  isPrivate: false,
};

export const upcomingBitbucketTraining: LiveTrainingCardMockProps = {
  instanceId: '100004',
  courseId: 'course-bitbucket-teams-001',
  title: 'Bitbucket for Teams',
  subtitle:
    'At vero eos et accusamus et iusto odio dignissimos ducimus qui blanditiis praesentium voluptatum deleniti atque corrupti quos dolores et quas molestias excepturi sint occaecati cupiditate non provident, similique sunt in culpa qui officia deserunt mollitia animi, id est laborum et dolorum fuga.',
  startDateTime: '2025-12-05T11:00:00-08:00' as ISODateTimeString, // December 5, 2025 at 11:00 AM PST
  endDateTime: '2025-12-05T13:00:00-08:00' as ISODateTimeString, // 2 hours
  status: 'upcoming' as TrainingStatus,
  enrolled: true,
  products: ['Bitbucket'],
  host: 'Sarah Williams',
  showAddToCalendar: true,
  showDownloadMaterials: false,
  courseMaterialsUrl: 'https://university.atlassian.com/student/path/4567890/materials',
  videoCallUrl: 'https://teams.microsoft.com/l/meetup-join/abc123',
  isFree: false,
  isSaved: false,
  isPrivate: true,
};

export const completedAgileJiraTraining: LiveTrainingCardMockProps = {
  instanceId: '100005',
  courseId: 'course-agile-jira-001',
  title: 'Agile with Jira',
  subtitle:
    'Temporibus autem quibusdam et aut officiis debitis aut rerum necessitatibus saepe eveniet ut et voluptates repudiandae sint et molestiae non recusandae. Itaque earum rerum hic tenetur a sapiente delectus, ut aut reiciendis voluptatibus maiores alias consequatur aut perferendis doloribus asperiores repellat.',
  startDateTime: '2025-10-25T10:00:00-07:00' as ISODateTimeString, // October 25, 2025 at 10:00 AM PDT
  endDateTime: '2025-10-25T12:30:00-07:00' as ISODateTimeString, // 2.5 hours
  status: 'completed',
  enrolled: true,
  products: ['Jira'],
  label: 'Team training',
  host: 'David Lee',
  showAddToCalendar: false,
  showDownloadMaterials: true,
  courseMaterialsUrl: 'https://university.atlassian.com/student/path/5678901/materials',
  videoCallUrl: 'https://meet.google.com/past-session-1',
  isFree: true,
  isSaved: false,
  isPrivate: true,
};

export const completedConfluenceAdvancedTraining: LiveTrainingCardMockProps = {
  instanceId: '100006',
  courseId: 'course-confluence-adv-001',
  title: 'Confluence Advanced',
  subtitle: 'Advanced content creation, templates, and collaboration features',
  startDateTime: '2025-10-18T15:00:00-07:00' as ISODateTimeString, // October 18, 2025 at 3:00 PM PDT
  endDateTime: '2025-10-18T18:00:00-07:00' as ISODateTimeString, // 3 hours
  status: 'completed',
  enrolled: true,
  products: ['Confluence'],
  label: 'Public class',
  host: 'Emily Chen',
  showAddToCalendar: false,
  showDownloadMaterials: true,
  courseMaterialsUrl: 'https://university.atlassian.com/student/path/6789012/materials',
  videoCallUrl: 'https://zoom.us/j/past-session-2',
  isFree: false,
  isSaved: false,
  isPrivate: false,
};

export const completedCloudMigrationTraining: LiveTrainingCardMockProps = {
  instanceId: '100007',
  courseId: 'course-cloud-migration-001',
  title: 'Atlassian Cloud Migration',
  subtitle: 'Best practices and strategies for migrating to Atlassian Cloud',
  startDateTime: '2025-10-10T13:00:00-07:00' as ISODateTimeString, // October 10, 2025 at 1:00 PM PDT
  endDateTime: '2025-10-10T14:00:00-07:00' as ISODateTimeString, // 1 hour
  status: 'completed',
  enrolled: true,
  products: ['Atlassian'],
  host: 'Alex Brown',
  showAddToCalendar: false,
  showDownloadMaterials: true,
  courseMaterialsUrl: 'https://university.atlassian.com/student/path/7890123/materials',
  videoCallUrl: 'https://meet.google.com/past-session-3',
  isFree: true,
  isSaved: false,
  isPrivate: false,
};

export const completedTrelloTraining: LiveTrainingCardMockProps = {
  instanceId: '100008',
  courseId: 'course-trello-pm-001',
  title: 'Trello for Project Management',
  subtitle: 'Organize and manage projects effectively using Trello boards and power-ups',
  startDateTime: '2025-09-28T14:30:00-07:00' as ISODateTimeString, // September 28, 2025 at 2:30 PM PDT
  endDateTime: '2025-09-28T16:30:00-07:00' as ISODateTimeString, // 2 hours
  status: 'completed',
  enrolled: true,
  products: ['Trello'],
  label: 'Public class',
  host: 'Rachel Green',
  showAddToCalendar: false,
  showDownloadMaterials: true,
  courseMaterialsUrl: 'https://university.atlassian.com/student/path/8901234/materials',
  videoCallUrl: 'https://teams.microsoft.com/l/meetup-join/past-session-4',
  isFree: false,
  isSaved: false,
  isPrivate: false,
};

export const upcomingJiraAlignTraining: LiveTrainingCardMockProps = {
  instanceId: '100009',
  courseId: 'course-jira-align-001',
  title: 'Jira Align for Enterprise',
  subtitle: 'Enterprise agile planning and portfolio management with Jira Align',
  startDateTime: '2025-12-10T10:00:00-08:00' as ISODateTimeString, // December 10, 2025 at 10:00 AM PST
  endDateTime: '2025-12-10T13:00:00-08:00' as ISODateTimeString, // 3 hours
  status: 'upcoming' as TrainingStatus,
  enrolled: false,
  products: ['Jira Align'],
  label: 'Team training',
  host: 'Michael Scott',
  showAddToCalendar: true,
  showDownloadMaterials: false,
  courseMaterialsUrl: 'https://university.atlassian.com/student/path/9012345/materials',
  videoCallUrl: 'https://zoom.us/j/enterprise-session',
  isFree: false,
  isSaved: false,
  isPrivate: true,
};

export const upcomingAutomationTraining: LiveTrainingCardMockProps = {
  instanceId: '100010',
  courseId: 'course-automation-001',
  title: 'Automation in Jira and Confluence',
  subtitle:
    'Nam libero tempore, cum soluta nobis est eligendi optio cumque nihil impedit quo minus id quod maxime placeat facere possimus, omnis voluptas assumenda est, omnis dolor repellendus. Temporibus autem quibusdam et aut officiis debitis aut rerum necessitatibus saepe eveniet ut et voluptates repudiandae sint et molestiae non recusandae.',
  startDateTime: '2025-12-15T09:30:00-08:00' as ISODateTimeString, // December 15, 2025 at 9:30 AM PST
  endDateTime: '2025-12-15T11:30:00-08:00' as ISODateTimeString, // 2 hours
  status: 'upcoming' as TrainingStatus,
  enrolled: true,
  products: ['Jira'],
  host: 'Lisa Anderson',
  showAddToCalendar: true,
  showDownloadMaterials: false,
  courseMaterialsUrl: 'https://university.atlassian.com/student/path/0123456/materials',
  videoCallUrl: 'https://meet.google.com/automation-training',
  isFree: true,
  isSaved: false,
  isPrivate: false,
};

// Collections of training sessions by status
export const upcomingTrainingSessions: LiveTrainingCardMockProps[] = [
  upcomingJiraAdminTraining,
  upcomingConfluenceTraining,
  upcomingJSMTraining,
  upcomingBitbucketTraining,
  upcomingJiraAlignTraining,
  upcomingAutomationTraining,
];

export const completedTrainingSessions: LiveTrainingCardMockProps[] = [
  completedAgileJiraTraining,
  completedConfluenceAdvancedTraining,
  completedCloudMigrationTraining,
  completedTrelloTraining,
];

export const enrolledTrainingSessions: LiveTrainingCardMockProps[] = [
  upcomingJiraAdminTraining,
  upcomingConfluenceTraining,
  upcomingBitbucketTraining,
  upcomingAutomationTraining,
  completedAgileJiraTraining,
  completedConfluenceAdvancedTraining,
  completedCloudMigrationTraining,
  completedTrelloTraining,
];

export const notEnrolledTrainingSessions: LiveTrainingCardMockProps[] = [
  upcomingJSMTraining,
  upcomingJiraAlignTraining,
];

export const allTrainingSessions: LiveTrainingCardMockProps[] = [
  ...upcomingTrainingSessions,
  ...completedTrainingSessions,
];

// Training sessions by product
export const jiraTrainingSessions: LiveTrainingCardMockProps[] = [
  upcomingJiraAdminTraining,
  completedAgileJiraTraining,
  upcomingAutomationTraining,
];

export const confluenceTrainingSessions: LiveTrainingCardMockProps[] = [
  upcomingConfluenceTraining,
  completedConfluenceAdvancedTraining,
];

export const jsmTrainingSessions: LiveTrainingCardMockProps[] = [upcomingJSMTraining];

export const bitbucketTrainingSessions: LiveTrainingCardMockProps[] = [upcomingBitbucketTraining];

// Training sessions by label type
export const teamTrainingSessions: LiveTrainingCardMockProps[] = [
  upcomingJiraAdminTraining,
  completedAgileJiraTraining,
  upcomingJiraAlignTraining,
];

export const publicClassSessions: LiveTrainingCardMockProps[] = [
  upcomingConfluenceTraining,
  upcomingJSMTraining,
  completedConfluenceAdvancedTraining,
  completedTrelloTraining,
];

// Mock response data structure (if needed for API mocking)
export interface LiveTrainingSessionData {
  id: string;
  contentfulId: string;
  courseTitleExternal: string;
  tagline: string;
  scheduledDate: string;
  status: 'upcoming' | 'completed';
  enrolled: boolean;
  product: string[];
  iltType: string[];
  host: string;
  slug: string;
  duration?: number;
  learningLevel?: string;
}

export const liveTrainingAPIResponseMock: LiveTrainingSessionData[] = [
  {
    id: 'ilt-1',
    contentfulId: 'ilt-advanced-jira-admin',
    courseTitleExternal: 'Advanced Jira Administration',
    tagline: 'Master advanced Jira configuration, automation, and administration best practices',
    scheduledDate: '2025-11-15T18:00:00Z',
    status: 'upcoming',
    enrolled: true,
    product: ['Jira'],
    iltType: ['Team training'],
    host: 'John Smith',
    slug: 'advanced-jira-admin',
    duration: 240,
    learningLevel: 'Advanced',
  },
  {
    id: 'ilt-2',
    contentfulId: 'ilt-confluence-fundamentals',
    courseTitleExternal: 'Confluence Fundamentals',
    tagline: 'Learn the basics of Confluence for effective team collaboration and documentation',
    scheduledDate: '2025-11-20T22:00:00Z',
    status: 'upcoming',
    enrolled: true,
    product: ['Confluence'],
    iltType: ['Public class'],
    host: 'Jane Doe',
    slug: 'confluence-fundamentals',
    duration: 180,
    learningLevel: 'Beginner',
  },
  {
    id: 'ilt-3',
    contentfulId: 'ilt-jsm-essentials',
    courseTitleExternal: 'Jira Service Management Essentials',
    tagline: 'ITSM best practices and implementation strategies with Jira Service Management',
    scheduledDate: '2025-12-01T17:00:00Z',
    status: 'upcoming',
    enrolled: false,
    product: ['Jira Service Management'],
    iltType: ['Public class'],
    host: 'Mike Johnson',
    slug: 'jsm-essentials',
    duration: 300,
    learningLevel: 'Intermediate',
  },
  {
    id: 'ilt-4',
    contentfulId: 'ilt-agile-jira',
    courseTitleExternal: 'Agile with Jira',
    tagline: 'Implementing agile methodologies and best practices using Jira',
    scheduledDate: '2025-10-25T17:00:00Z',
    status: 'completed',
    enrolled: true,
    product: ['Jira'],
    iltType: ['Team training'],
    host: 'David Lee',
    slug: 'agile-jira',
    duration: 240,
    learningLevel: 'Intermediate',
  },
  {
    id: 'ilt-5',
    contentfulId: 'ilt-confluence-advanced',
    courseTitleExternal: 'Confluence Advanced',
    tagline: 'Advanced content creation, templates, and collaboration features',
    scheduledDate: '2025-10-18T22:00:00Z',
    status: 'completed',
    enrolled: true,
    product: ['Confluence'],
    iltType: ['Public class'],
    host: 'Emily Chen',
    slug: 'confluence-advanced',
    duration: 180,
    learningLevel: 'Advanced',
  },
];

// Helper function to convert API response to card props
export const convertToCardProps = (apiData: LiveTrainingSessionData): LiveTrainingCardMockProps => {
  const startDate = new Date(apiData.scheduledDate);
  // Default to 2 hour duration if not specified
  const duration = apiData.duration || 120;
  const endDate = new Date(startDate.getTime() + duration * 60 * 1000);

  return {
    instanceId: apiData.id,
    courseId: `course-${apiData.contentfulId}`,
    title: apiData.courseTitleExternal,
    subtitle: apiData.tagline,
    startDateTime: startDate.toISOString() as ISODateTimeString,
    endDateTime: endDate.toISOString() as ISODateTimeString,
    status: apiData.status as TrainingStatus,
    enrolled: apiData.enrolled,
    products: apiData.product,
    label: apiData.iltType[0],
    host: apiData.host,
    showAddToCalendar: apiData.status === 'upcoming',
    showDownloadMaterials: apiData.status === 'completed' && apiData.enrolled,
    courseMaterialsUrl: `https://university.atlassian.com/student/path/${apiData.id}/materials`,
    isFree: false,
    isSaved: false,
    isPrivate: apiData.iltType[0] === 'Team training',
  };
};

// Convert all API mock data to card props
export const liveTrainingCardPropsMock: LiveTrainingCardMockProps[] =
  liveTrainingAPIResponseMock.map(convertToCardProps);

/**
 * Converts LiveTrainingCardMockProps to FlattenedLearningInstance format
 * This matches the expected format from the ALM API transformer
 */
export const convertToFlattenedInstance = (cardProps: LiveTrainingCardMockProps): FlattenedLearningInstance => ({
  instance: {
    id: cardProps.instanceId,
    type: 'learningObjectInstance',
    name: cardProps.title,
    startDateTime: cardProps.startDateTime,
    endDateTime: cardProps.endDateTime,
    status: cardProps.status,
    enrolled: cardProps.enrolled,
    product: cardProps.products[0] || '',
    label: cardProps.label,
    host: cardProps.host,
    videoCallUrl: cardProps.videoCallUrl,
    isFree: cardProps.isFree,
    isPrivate: cardProps.isPrivate,
    courseMaterialsUrl: cardProps.courseMaterialsUrl,
  },
  id: cardProps.courseId,
  type: 'learningObject',
  title: cardProps.title,
  description: cardProps.subtitle,
  tags: cardProps.products,
  courseMaterialsUrl: cardProps.courseMaterialsUrl,
});

// Convert all training sessions to FlattenedLearningInstance format for use in loader
export const allTrainingSessionsFlattened: FlattenedLearningInstance[] =
  allTrainingSessions.map(convertToFlattenedInstance);
